import { create } from 'zustand';
import { logger } from '../utils/logger';

/**
 * UI-only state for dashboard
 * This store handles UI state that doesn't need to be persisted to the server
 * Server state is managed by React Query
 * 
 * Use this store for:
 * - Dashboard filtering UI state
 * - User filter selections
 * - Project filter selections  
 * - Tag filter selections
 * - Filter persistence to localStorage
 */
interface DashboardUIState {
  // Active filters
  activeFilters: {
    userIds: string[];
    projectIds: string[];
    tags: string[];
  };
  
  // Filter state persistence key
  filterStorageKey: 'dashboard-filters';
  
  // Filter actions
  setUserFilter: (userIds: string[]) => void;
  setProjectFilter: (projectIds: string[]) => void;
  setTagFilter: (tags: string[]) => void;
  toggleUserFilter: (userId: string) => void;
  toggleProjectFilter: (projectId: string) => void;
  toggleTagFilter: (tag: string) => void;
  resetFilters: (currentUserId?: string | null) => void;
  
  // Persistence actions
  loadFiltersFromStorage: () => void;
  saveFiltersToStorage: () => void;
  
  // Initialization
  initializeFilters: (currentUserId: string | null) => void;
  
  // Reset all state
  resetDashboardUIState: () => void;
}

/**
 * Store for managing dashboard UI state
 * This only handles UI-specific state that doesn't need to be persisted to the server
 * 
 * @example
 * // Using the store in a component
 * const { 
 *   activeFilters,
 *   setUserFilter,
 *   setTagFilter,
 *   resetFilters
 * } = useDashboardUIStore();
 * 
 * // Set active user filters
 * const handleUserFilterChange = (userIds) => {
 *   setUserFilter(userIds);
 * };
 * 
 * // Toggle a single tag filter
 * const handleTagToggle = (tag) => {
 *   useDashboardUIStore.getState().toggleTagFilter(tag);
 * };
 * 
 * // Load persisted filters on mount
 * useEffect(() => {
 *   useDashboardUIStore.getState().loadFiltersFromStorage();
 * }, []);
 * 
 * // Save filters to localStorage when they change
 * useEffect(() => {
 *   useDashboardUIStore.getState().saveFiltersToStorage();
 * }, [activeFilters]);
 */
export const useDashboardUIStore = create<DashboardUIState>((set, get) => ({
  // Default filter state
  activeFilters: {
    userIds: [],
    projectIds: [],
    tags: [],
  },
  
  // Storage key for persistence
  filterStorageKey: 'dashboard-filters',
  
  // Filter actions
  setUserFilter: (userIds) => {
    set((state) => ({
      activeFilters: {
        ...state.activeFilters,
        userIds,
      },
    }));
    
    logger.component('Updated dashboard user filters', { userCount: userIds.length });
  },
  
  setProjectFilter: (projectIds) => {
    set((state) => ({
      activeFilters: {
        ...state.activeFilters,
        projectIds,
      },
    }));
    
    logger.component('Updated dashboard project filters', { projectCount: projectIds.length });
  },
  
  setTagFilter: (tags) => {
    set((state) => ({
      activeFilters: {
        ...state.activeFilters,
        tags,
      },
    }));
    
    logger.component('Updated dashboard tag filters', { tagCount: tags.length });
  },
  
  toggleUserFilter: (userId) => {
    set((state) => {
      const userIds = [...state.activeFilters.userIds];
      const index = userIds.indexOf(userId);
      
      if (index === -1) {
        userIds.push(userId);
      } else {
        userIds.splice(index, 1);
      }
      
      return {
        activeFilters: {
          ...state.activeFilters,
          userIds,
        },
      };
    });
  },
  
  toggleProjectFilter: (projectId) => {
    set((state) => {
      const projectIds = [...state.activeFilters.projectIds];
      const index = projectIds.indexOf(projectId);
      
      if (index === -1) {
        projectIds.push(projectId);
      } else {
        projectIds.splice(index, 1);
      }
      
      return {
        activeFilters: {
          ...state.activeFilters,
          projectIds,
        },
      };
    });
  },
  
  toggleTagFilter: (tag) => {
    set((state) => {
      const tags = [...state.activeFilters.tags];
      const index = tags.indexOf(tag);
      
      if (index === -1) {
        tags.push(tag);
      } else {
        tags.splice(index, 1);
      }
      
      return {
        activeFilters: {
          ...state.activeFilters,
          tags,
        },
      };
    });
  },
  
  resetFilters: (currentUserId?: string | null) => {
    // If currentUserId is provided, set it as the default user filter
    // This matches the current behavior where reset sets the current user as filter
    const userIds = currentUserId ? [currentUserId] : [];
    
    set({
      activeFilters: {
        userIds,
        projectIds: [],
        tags: [],
      },
    });
    
    // Clear persisted filters when resetting
    try {
      localStorage.removeItem(get().filterStorageKey);
    } catch (error) {
      logger.error('Failed to clear dashboard filters from storage', error);
    }
    
    logger.component('Reset dashboard filters', { defaultUserId: currentUserId });
  },
  
  // Persistence actions
  loadFiltersFromStorage: () => {
    try {
      const storedFilters = localStorage.getItem(get().filterStorageKey);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        set({
          activeFilters: {
            userIds: parsedFilters.userIds || [],
            projectIds: parsedFilters.projectIds || [],
            tags: parsedFilters.tags || [],
          },
        });
        
        logger.component('Loaded dashboard filters from storage', parsedFilters);
      }
    } catch (error) {
      logger.error('Failed to load dashboard filters from storage', error);
    }
  },
  
  saveFiltersToStorage: () => {
    try {
      const { activeFilters, filterStorageKey } = get();
      localStorage.setItem(filterStorageKey, JSON.stringify(activeFilters));
      
      logger.component('Saved dashboard filters to storage', activeFilters);
    } catch (error) {
      logger.error('Failed to save dashboard filters to storage', error);
    }
  },
  
  initializeFilters: (currentUserId: string | null) => {
    // First try to load from storage
    const storedFilters = localStorage.getItem(get().filterStorageKey);
    
    if (storedFilters) {
      try {
        const parsedFilters = JSON.parse(storedFilters);
        set({
          activeFilters: {
            userIds: parsedFilters.userIds || [],
            projectIds: parsedFilters.projectIds || [],
            tags: parsedFilters.tags || [],
          },
        });
        logger.component('Initialized dashboard filters from storage', parsedFilters);
        return;
      } catch (error) {
        logger.error('Failed to parse stored filters', error);
      }
    }
    
    // No stored filters or parse error - initialize with current user
    if (currentUserId) {
      set({
        activeFilters: {
          userIds: [currentUserId],
          projectIds: [],
          tags: [],
        },
      });
      logger.component('Initialized dashboard filters with current user', { currentUserId });
    }
  },
  
  // Reset all state
  resetDashboardUIState: () => {
    set({
      activeFilters: {
        userIds: [],
        projectIds: [],
        tags: [],
      },
    });
    
    // Also clear from localStorage
    try {
      localStorage.removeItem(get().filterStorageKey);
    } catch (error) {
      logger.error('Failed to clear dashboard filters from storage', error);
    }
    
    logger.component('Reset dashboard UI state');
  },
}));